import { Component, computed, EventEmitter, HostListener, inject, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { CollaboratorService } from '../../../../services/collaborator/collaborator.service';
import { BriefResponseDto, SocialChannelEnum } from '../../../../common/general-types/briefs.dto';
import { summariseBriefAsk } from '@/app/common/brief-ask/summarise-brief-ask';
import { CampaignWithCountDto } from '../../../../common/general-types/campaigns.dto';
import { VideoFormatEnum } from '../../../../common/general-types/briefs.dto';
import { CreatorResponseDto } from '../../../../common/general-types/creators.dto';
import { BriefService } from '../../../../services/brief/brief.service';
import { Subscription, take } from 'rxjs';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { CampaignService } from '../../../../services/campaign/campaign.service';
import { LoadingInlineComponent } from '../../../../atoms/loading-inline/loading-inline.component';
import { DecimalPipe } from '@angular/common';
import { LdgImgComponent } from '../../../../atoms/ldg-img/ldg-img.component';
import { ImageService } from '../../../../services/image/image.service';
import { FormatService } from '../../../../common/format/format.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { CreateBriefApplicationDto } from '../../../../common/general-types/brief-application.dto';
import { PricingModelV2Dto } from '../../../../common/general-types/pricing.dto';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { ViewportOverlayDirective } from '../../../../common/directives/viewport-overlay/viewport-overlay.directive';
import { averageReviewRating, briefDurationLabel, briefStatusLabel, creatorReviewRating } from './collaborators.helpers';

@Component({
  selector: 'cc-collaborators',
  standalone: true,
  imports: [LoadingInlineComponent, DecimalPipe, LdgImgComponent],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './collaborators.component.html',
  styleUrl: './collaborators.component.css'
})
export class CollaboratorsComponent implements OnInit, OnDestroy {

  @HostListener('document:keydown.escape', ['$event' as any])
  handleEscapeKey(event: KeyboardEvent) {
    this.closeCollaborators();
  }

  @Output() close = new EventEmitter<void>();

  campaigns = signal<CampaignWithCountDto[] | undefined>(undefined);
  allBriefs = signal<{ data: BriefResponseDto[], count: number } | undefined>(undefined);
  displayBriefs = signal<BriefResponseDto[] | undefined>(undefined);

  selectCampaignOpen = signal<boolean>(false);
  selectedCampaign = signal<CampaignWithCountDto | undefined>(undefined);

  selectedBriefs = signal<BriefResponseDto[]>([]);
  specificBriefSelected = signal<BriefResponseDto | undefined>(undefined);

  confirmInvite = signal<boolean>(false);
  isProcessingInvites = signal<boolean>(false);
  subscriptionList = new Subscription();

  readonly selectedBriefCount = computed(() => this.selectedBriefs().length);
  readonly canInvite = computed(() =>
    this.selectedBriefs().length > 0 && this.collaboratorsService.selectedCreators().length > 0
  );


  protected readonly VideoFormatEnum = VideoFormatEnum;

  public collaboratorsService = inject(CollaboratorService);
  private briefService = inject(BriefService);
  private currentUserService = inject(CurrentUserService);
  private campaignService = inject(CampaignService);
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  private alertService = inject(AlertService);
  private firebaseService = inject(FirebaseService);
  private briefApplicationService = inject(BriefApplicationService);


  ngOnInit(): void {
    if (this.collaboratorsService.briefId()) {
      this.getBrief(this.collaboratorsService.briefId()!)
    }

    this.getAllCampaigns();
    this.getBriefs();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getBrief(briefId: string) {
    this.subscriptionList.add(
      this.briefService.getBriefById(briefId).pipe(take(1)).subscribe((brief) => {
        this.selectedBriefs.set([brief]);
        this.specificBriefSelected.set(JSON.parse(JSON.stringify(brief)));
      }),
    );
  }

  getAllCampaigns() {
    this.subscriptionList.add(
      this.campaignService.getAllBusinessCampaigns(this.currentUserService.focussedBrand.id).pipe(take(1)).subscribe((response) => {
        this.campaigns.set(response as CampaignWithCountDto[]);
      }),
    );
  }

  getBriefs() {
    this.subscriptionList.add(
      this.briefService.getBriefsForBusinessAccount(this.currentUserService.focussedBrand.id, {
        statuses: [0, 1]
      }).pipe(take(1)).subscribe((response: { data: BriefResponseDto[], count: number } | BriefResponseDto[]) => {
        const data = Array.isArray(response) ? response : (response?.data ?? []);
        this.allBriefs.set({ data, count: data.length });
        this.displayBriefs.set(this.briefsForSelectedCampaign(data));
      }),
    );
  }

  getPotentialReach() {
    if (this.collaboratorsService.selectedCreators() && this.collaboratorsService.selectedCreators()!.length > 0) {
      return this.collaboratorsService.selectedCreators().reduce((acc: number, creator: CreatorResponseDto) => acc + creator.instaFollowers + creator.tikTokFollowers + creator.youtubeSubscribers, 0);
    } else {
      return 0;
    }
  }

  getAverageEngagement() {
    if (this.collaboratorsService.selectedCreators() && this.collaboratorsService.selectedCreators()!.length > 0) {
      return this.collaboratorsService.selectedCreators().reduce((acc: number, creator: CreatorResponseDto) => acc + this.getAvgEngaegement(creator), 0) / this.collaboratorsService.selectedCreators().length;
    } else {
      return 0;
    }
  }

  getAvgEngaegement(creator: CreatorResponseDto) {
    if (creator && creator.ssCreator) {
      if (creator.ssCreator.instagramAverages && creator.ssCreator.tiktokAverages) {
        return ((creator.ssCreator.instagramAverages?.averageEngagementRate || 0) + (creator.ssCreator.tiktokAverages?.averageEngagementRate || 0)) / 2
      } else if (creator.ssCreator.instagramAverages) {
        return (creator.ssCreator.instagramAverages?.averageEngagementRate || 0)
      } else if (creator.ssCreator.tiktokAverages) {
        return (creator.ssCreator.tiktokAverages?.averageEngagementRate || 0)
      } else {
        return 0
      }
    } else {
      return 0
    }
  }

  /** Mean review rating of the selected creators who have reviews; null when none do. */
  getAverageRating(): number | null {
    return averageReviewRating(this.collaboratorsService.selectedCreators() ?? []);
  }

  creatorRating(creator: CreatorResponseDto): number | null {
    return creatorReviewRating(creator);
  }

  getBriefStatus(): string {
    return briefStatusLabel(this.specificBriefSelected());
  }

  getVideoFormat() {
    if (this.specificBriefSelected() && this.specificBriefSelected()?.currentPrice) {
      return VideoFormatEnum[this.specificBriefSelected()?.videoFormat || 0];
    } else {
      return '-'
    }
  }

  getVideoDuration(): string {
    return briefDurationLabel(this.specificBriefSelected());
  }

  briefAsk() {
    return summariseBriefAsk(this.specificBriefSelected() ?? {});
  }

  getSocialChannel() {
    if (this.specificBriefSelected() && this.specificBriefSelected()?.shoutBrief) {
      return SocialChannelEnum[this.specificBriefSelected()?.shoutBrief?.socialPlatform || 0] || 'Unknown';
    } else {
      return 'Unknown';
    }
  }

  async clearSelection() {
    const confirm = await this.alertService.confirm({
      title: 'Clear Selection?',
      message: 'Are you sure you want to clear the selection?',
      confirmText: 'Clear Selection',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'info'
    });

    if (confirm) {
      this.collaboratorsService.selectedCreators.set([]);
      this.closeCollaborators();
    }
  }

  selectCampaign(campaign: CampaignWithCountDto) {
    this.selectedCampaign.set(campaign);
    this.selectCampaignOpen.set(false);
    this.displayBriefs.set(this.briefsForSelectedCampaign(this.allBriefs()?.data ?? []));
  }

  private briefsForSelectedCampaign(briefs: BriefResponseDto[]): BriefResponseDto[] {
    const campaign = this.selectedCampaign();
    if (!campaign) {
      return briefs;
    }

    return briefs.filter((brief) => this.briefBelongsToCampaign(brief, campaign));
  }

  private briefBelongsToCampaign(brief: BriefResponseDto, campaign: CampaignWithCountDto): boolean {
    return brief.campaignId === campaign.id || brief.campaign?.id === campaign.id;
  }

  isBriefSelected(brief: BriefResponseDto): boolean {
    return this.selectedBriefs().some((selected) => selected.id === brief.id);
  }

  selectBrief(brief: BriefResponseDto) {
    this.selectedBriefs.update((current) => {
      if (current.some((selected) => selected.id === brief.id)) {
        return current.filter((selected) => selected.id !== brief.id);
      }
      return [...current, brief];
    });
  }

  inviteCreators() {
    if (this.canInvite()) {
      this.confirmInvite.set(true);
    }
  }

  confirmInvitations() {
    const briefs = this.selectedBriefs();
    const creators = this.collaboratorsService.selectedCreators();
    if (briefs.length === 0 || creators.length === 0) {
      return;
    }

    this.isProcessingInvites.set(true);

    const applications: CreateBriefApplicationDto[] = [];
    for (const brief of briefs) {
      for (const creator of creators) {
        applications.push({
          briefId: brief.id,
          creatorUserId: creator.blazeFirebaseUid,
          firstName: creator.firstName,
          lastName: creator.lastName,
          applicationStatus: 1,
          hiredPricing: brief.basePricingModel as PricingModelV2Dto,
          createdBy: this.firebaseService.userId!
        });
      }
    }

    this.subscriptionList.add(
      this.briefApplicationService.createBulkBriefApplications(applications).pipe(take(1)).subscribe({
        next: () => {
          const invitedUids = creators.map((creator) => creator.blazeFirebaseUid);
          for (const brief of briefs) {
            this.collaboratorsService.markInvited(brief.id, invitedUids);
          }
          this.confirmInvite.set(false);
          this.selectedBriefs.set([]);
          this.collaboratorsService.selectedCreators.set([]);
          this.isProcessingInvites.set(false);
          this.closeCollaborators();
        },
        error: () => {
          this.isProcessingInvites.set(false);
        },
      }),
    );
  }

  closeCollaborators() {
    this.collaboratorsService.showCollaborators.set(false);
  }
}
